import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { MessageCircle, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useMeta, pkr, waLink } from "@/lib/site";
import { Overline, Reveal } from "@/components/Reveal";
import { FragranceCard } from "@/components/FragranceCard";
import { ReviewSummary, ReviewsSection } from "@/components/Reviews";

const Meter = ({ label, value }) => (
  <div>
    <div className="flex justify-between text-[11px] tracking-[0.2em] uppercase text-[#6E6151] mb-2">
      <span>{label}</span>
      <span className="text-[#5A4C3C]">{value}/5</span>
    </div>
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className={`h-[3px] flex-1 ${i <= value ? "bg-[#8F6B3A]" : "bg-white/10"}`} />
      ))}
    </div>
  </div>
);

const Pyramid = ({ notes, hex }) => (
  <div className="space-y-8">
    {[["Top Notes", notes?.top, "The first impression"], ["Heart Notes", notes?.heart, "The soul of the scent"], ["Base Notes", notes?.base, "What lingers, hours later"]].map(([label, list, sub]) => (
      <div key={label} className="border-l pl-6" style={{ borderColor: `${hex}66` }}>
        <p className="text-[11px] tracking-[0.3em] uppercase text-[#8F6B3A]">{label}</p>
        <p className="font-display text-xl text-[#2B2118] mt-1.5">{(list || []).join(" · ")}</p>
        <p className="text-xs text-[#6E6151] mt-1 italic">{sub}</p>
      </div>
    ))}
  </div>
);

export default function FragranceDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const { data: f } = useQuery({ queryKey: ["fragrance", slug], queryFn: async () => (await api.get(`/fragrances/${slug}`)).data });
  const { data: all = [] } = useQuery({ queryKey: ["fragrances"], queryFn: async () => (await api.get("/fragrances")).data });
  useMeta(f?.name, f ? `${f.name} — ${f.tagline}. ${pkr(f.price)}, 50ml Eau de Parfum by Bonheur Eternal. Delivered all over Pakistan.` : undefined);

  useEffect(() => {
    if (!f) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "Product", name: `${f.name} Eau de Parfum 50ml`,
      brand: { "@type": "Brand", name: "Bonheur Eternal" }, description: f.tagline, image: f.image,
      offers: { "@type": "Offer", priceCurrency: "PKR", price: f.price, availability: "https://schema.org/InStock" },
    });
    document.head.appendChild(s);
    return () => s.remove();
  }, [f]);

  if (!f) return <div className="min-h-screen" />;
  const related = all.filter((x) => (f.related || []).includes(x.slug)).slice(0, 3);
  const waMsg = `Hello Bonheur Eternal, I'd like to order ${f.name} (50ml Eau de Parfum) — ${pkr(f.price)}. Please confirm availability and delivery details.`;

  return (
    <div className="pt-16 md:pt-20">
      <section className="relative overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full blur-3xl opacity-25 pointer-events-none" style={{ background: f.hex }} />
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start relative">
          <motion.div initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}>
            <div className="border border-[#2B2118]/[0.08] overflow-hidden relative" style={{ boxShadow: `0 0 120px -20px ${f.hex}55` }}>
              <img src={f.image} alt={f.name} className="w-full object-cover" style={{ aspectRatio: "1408/1097" }} data-testid="fragrance-detail-image" />
            </div>
            {(f.gallery || []).length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-3">
                {f.gallery.map((g, i) => (
                  <img key={i} src={g} alt="" loading="lazy" className="w-full aspect-square object-cover border border-[#2B2118]/[0.07]" />
                ))}
              </div>
            )}
          </motion.div>
          <div>
            <Reveal>
              <Overline>{f.gender} — Eau de Parfum · 50ml / 1.7 fl oz</Overline>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2B2118] mt-4 tracking-tight" data-testid="fragrance-detail-name">{f.name}</h1>
              <ReviewSummary slug={f.slug} />
              <p className="mt-4 text-[#5A4C3C] leading-loose max-w-md">{f.description}</p>
              <p className="mt-8 font-display text-3xl text-[#8F6B3A]" data-testid="fragrance-detail-price">{pkr(f.price)}</p>
              <div className="mt-8 flex flex-wrap gap-4">
                {f.sold_out ? (
                  <div data-testid="sold-out-block">
                    <p className="font-display text-2xl text-[#2B2118] italic">Currently sold out — back soon.</p>
                    <a
                      href={waLink(`Hello Bonheur Eternal, please let me know when ${f.name} is back in stock.`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="notify-whatsapp-btn"
                      className="mt-6 inline-flex items-center gap-3 rounded-full border border-[#8F6B3A]/50 text-[#8F6B3A] px-10 py-4 text-xs tracking-[0.2em] uppercase hover:bg-[#8F6B3A] hover:text-[#FBF8F1] transition-colors duration-300"
                    >
                      <MessageCircle size={16} strokeWidth={1.5} /> Notify me on WhatsApp
                    </a>
                  </div>
                ) : (
                  <>
                    <button
                      data-testid="add-to-cart-btn"
                      onClick={() => { add({ slug: f.slug, name: f.name, price: f.price, image: f.image }); toast.success(`${f.name} added to your cart.`); }}
                      className="inline-flex items-center gap-3 rounded-full bg-[#8F6B3A] text-[#FBF8F1] px-10 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors duration-300"
                    >
                      <ShoppingBag size={16} strokeWidth={1.5} /> Add to Cart
                    </button>
                    <a
                      href={waLink(waMsg)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="order-whatsapp-btn"
                      className="inline-flex items-center gap-3 rounded-full border border-[#8F6B3A]/50 text-[#8F6B3A] px-10 py-4 text-xs tracking-[0.2em] uppercase hover:bg-[#8F6B3A] hover:text-[#FBF8F1] transition-colors duration-300"
                    >
                      <MessageCircle size={16} strokeWidth={1.5} /> Order via WhatsApp
                    </a>
                  </>
                )}
              </div>
              <p className="mt-4 text-xs text-[#6E6151]">Cash on delivery · All over Pakistan · Replies within hours</p>
            </Reveal>
            <Reveal delay={0.15} className="mt-14">
              <Pyramid notes={f.notes} hex={f.hex} />
            </Reveal>
            <Reveal delay={0.2} className="mt-14 grid grid-cols-2 gap-x-10 gap-y-8">
              <Meter label="Longevity" value={f.longevity} />
              <Meter label="Sillage" value={f.sillage} />
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#6E6151] mb-2">Occasion</p>
                <p className="text-sm text-[#2B2118]">{f.occasion}</p>
              </div>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#6E6151] mb-2">Season</p>
                <p className="text-sm text-[#2B2118]">{f.season}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 border-t border-[#2B2118]/[0.06]">
          <Reveal>
            <h2 className="font-display text-3xl text-[#2B2118] tracking-tight mb-14">You may also like</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16" data-testid="related-fragrances">
            {related.map((r, i) => (
              <FragranceCard key={r.slug} f={r} index={i} />
            ))}
          </div>
        </section>
      )}
      <ReviewsSection slug={f.slug} fragranceName={f.name} />
    </div>
  );
}
